import React, {
	ChangeEvent,
	useState,
	useReducer,
	FormEvent,
	useEffect,
} from 'react';
import FatigueLifeModal from "./FatigueLifeModal";
import {
	bearingCategory,
	lifeAdjustmentList,
	lubricantType,
	text,
	unitList,
	unitMap,
} from '../data';
import PartSearch from '../../components/bearing-search/components/PartSearch';
import {
	BearingCalculationData,
	BearingFatigueLifePayload,
	BearingInfo,
	BearingTypeCode,
	TimkenLifeAdjustment,
	TimkenLubricant,
	TimkenLubricantType,
	TimkenOption,
	TimkenUnit,
} from '../../components/bearing-search/types/types';
import {
	CONTAMINATION_FACTOR_MAX,
	CONTAMINATION_FACTOR_MIN,
	getFatigueLife,
	getLubricantInfo,
	lubricants,
} from '../../components/bearing-search/api/api';
import CalculationResults from '../components/CalculationResults';
import partReducer, {
	BearingPartState,
	initialBearingPartState,
} from '../../components/bearing-search/reducers/reducer';
import fatigueLifeReducer, { initialFatigueState } from './reducer';
import {
	getDimensionLabel,
	getPartStateFromParams,
	getTooltip,
	toggleImportantNote,
	toggleNextTools,
	updateToolParams,
	getPartNumberFromUrl,
	toggleReportReady,
} from '../utils/engineering-tools';
import { SelectOption } from '../types/types';
import { isNonZero, isValidRange } from '../utils/validations';
import TextInput from '../../components/bearing-search/components/forms/TextInput';
import SelectDropdown from '../../components/bearing-search/components/forms/SelectDropdown';
import NumberInput from '../../components/bearing-search/components/forms/NumberInput';
import {
	trackResults,
	trackStart,
	updateActionParams,
} from '../utils/analytics';
import PartPreSearch from '../components/PartPreSearch';

const FatigueLife: React.FC = () => {
	const [ isPartSelected, setIsPartSelected ] = useState<boolean>(null);
	const [ isPartOpen, setIsPartOpen ] = useState( false );
	const [ isCalculated, setIsCalculated ] = useState( false );
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ searchInput, setSearchInput ] = useState( '' );
	const [ results, setResults ] = useState<Array<BearingCalculationData | null>>(
		[],
	);
	const [ lubricantList, setLubricantList ] = useState<SelectOption<string>[]>(
		[],
	);
	const [ partState, partDispatch ] = useReducer(
		partReducer,
		initialBearingPartState,
	);
	const [ dimensionLabel, setDimensionLabel ] = useState( '' );
	const [ formState, dispatch ] = useReducer(
		fatigueLifeReducer,
		initialFatigueState,
	);

	// Build lubricants options
	useEffect( () => {
		trackStart( 'bearingFatigueLife' );

		setLubricantList(
			lubricants.map( ( lub: TimkenLubricant ) => {
				return {
					id: `lub_${ lub.name.toLowerCase() }`,
					value: lub.name,
					label: lub.name,
				} as SelectOption<string>;
			} ),
		);

		selectLubricant( lubricants.find(({ name }) => name === 'ISOVG22')?.name || lubricants[ 0 ]?.name || '' );

		const queryMap = new URLSearchParams( document.location.search );
		const savedPart = getPartStateFromParams( queryMap );

		if (queryMap.size == 1) {
			const partNumberFromUrl = getPartNumberFromUrl();
			if (partNumberFromUrl) {
				setSearchInput(partNumberFromUrl);
				partDispatch({
					type: 'select_part',
					payload: { marketPlaceDescription: partNumberFromUrl },
				});
				setIsPartSelected( true );
				handleSubmit();
			}
		} else if (queryMap.size > 1) {
			if ( savedPart.partNumber ) {
				partDispatch( {
					type: 'reset',
					payload: savedPart,
				} );
				setIsPartSelected( true );
				setSearchInput( savedPart.marketPlaceDescription || '' );
			}
		}
	}, [] );

	// Pass down part number to next recommended tool
	useEffect( () => {
		updateToolParams<BearingPartState>( partState );

		return () => {
			updateToolParams();
		};
	}, [ partState.marketPlaceDescription ] );

	// Show / hide recommended tools
	useEffect( () => {
		const isShown = isCalculated && ! isResultsLoading;
		toggleNextTools( isShown );
		toggleReportReady(
			isShown,
			partState.marketPlaceDescription,
			partState.primaryImage,
		);

		return () => toggleNextTools( false );
	}, [ isCalculated, isResultsLoading ] );

	const inputPartNumber = ( event: ChangeEvent<HTMLInputElement> ) => {
		const value = event.target.value;
		setSearchInput( value );
		setIsPartSelected( false );
	};

	const verifyPart = ( part?: BearingInfo ) => {
		if ( part ) {
			partDispatch( {
				type: 'select_part',
				payload: part,
			} );
			setIsPartSelected( true );
			setSearchInput( part.marketPlaceDescription || '' );
		}

		setIsPartOpen( false );
	};

	const clearInputs = () => {
		setIsCalculated( false );
		toggleImportantNote( false );

		const currentOption = formState.option;
		const currentUnit = formState.unit;

		dispatch( {
			type: 'reset',
		} );

		selectLubricant( lubricants.find(({ name }) => name === 'ISOVG22')?.name || lubricants[ 0 ]?.name || '' );
		selectBearingOption(null, currentOption);
		selectUnit(currentUnit);
	};

	const clearPn = () => {
		setSearchInput( '' );
		setIsCalculated( false );
		setIsPartSelected( false );
		toggleImportantNote( false );

		const currentOption = formState.option;

		partDispatch( {
			type: 'reset',
		} );

		selectBearingOption(null, currentOption);
	};

	const selectBearingOption = ( event?: ChangeEvent<HTMLInputElement>, option?: TimkenOption) => {
		let selected = '';

		if (event) {
			selected = event.target.value as TimkenOption;
		} else if (option) {
			selected = option;
		}

		dispatch( {
			type: 'change_option',
			payload: selected,
		} );
	};

	const selectUnit = (unit?: TimkenUnit | unknown) => {
		dispatch( {
			type: 'select_unit',
			payload: unit,
		});
	};

	const selectLifeAdjustment = ( event: ChangeEvent<HTMLSelectElement> ) => {
		const value = event.target.value as unknown as TimkenLifeAdjustment;

		dispatch( {
			type: 'change_life_adjustment',
			payload: value,
		} );
	};

	const selectLubricant = ( name: string ) => {
		const lubricant = getLubricantInfo( name );

		if ( lubricant ) {
			const {
				lubricantType: lubeIndex,
				lubricantCST40,
				lubricantCST100,
				lubricantSUS100,
				lubricantSUS210,
			} = lubricant;

			dispatch( {
				type: 'change_lub_name',
				payload: name,
			} );

			dispatch( {
				type: 'change_lub_index',
				payload: lubeIndex,
			} );

			dispatch( {
				type: 'change_number_field',
				payload: [ 'lubricantSUS100', lubricantSUS100 ],
			} );

			dispatch( {
				type: 'change_number_field',
				payload: [ 'lubricantSUS210', lubricantSUS210 ],
			} );

			dispatch( {
				type: 'change_number_field',
				payload: [ 'lubricantCST100', lubricantCST100 ],
			} );

			dispatch( {
				type: 'change_number_field',
				payload: [ 'lubricantCST40', lubricantCST40 ],
			} );
		}
	};

	const handleSubmit = ( event?: FormEvent ) => {
		if (event) {
			event.preventDefault();
		}

		if ( ! partState.partNumber ) {
			// Search Part
			setIsPartOpen( true );
		} else {
			// Validate form
			dispatch( {
				type: 'change_validation_flag',
				payload: true,
			} );

			if ( formState.requestData ) {
				// Calculate Bearing Fatigue Life
				setIsCalculated( true );
				setIsResultsLoading( true );
				toggleImportantNote( false );

				const { partNumber, cupPartNumber, bearingType, bearingSubType } =
					partState;

				const request: Partial<BearingFatigueLifePayload> = {
					partNumber,
					cupPartNumber,
					bearingType,
					bearingSubType,
					...formState.requestData,
				};

				getFatigueLife( request )
					.then( ( response ) => {
						updateActionParams( 'bearingFatigueLife', partState );

						let reportData: Array<BearingCalculationData | null> = [];

						if ( response.report && response.report.length > 0 ) {
							reportData = response.report[ 0 ].data;
						}

						setResults( reportData );
						trackResults( 'bearingFatigueLife', request, reportData.length, partState.marketPlaceDescription );
						setDimensionLabel( getDimensionLabel( partState, formState.unit ) );
						toggleImportantNote( true );

						document.querySelector('.t-engineering-tools__results').scrollIntoView({
							behavior: 'smooth',
						});
					} )
					.finally( () => setIsResultsLoading( false ) );
			}
		}
	};

	const handleFormChange = () => {
		dispatch( {
			type: 'change_validation_flag',
			payload: false,
		} );

		dispatch( {
			type: 'update_request_data',
		} );
	};

	const preSearchClickHandler = () => {
		setIsPartSelected(true);
	};

	const setPartFromPreSearchAndSubmit = (value: string, e: React.MouseEvent<HTMLElement>) => {
		setSearchInput( value );
		handleSubmit(e);
	};

	return (
		<>
			<div>
				<form onSubmit={handleSubmit} onChange={handleFormChange}>
					<div className="t-engineering-tools__options t-engineering-tools__options--inline">
						{bearingCategory.map( ( option ) => {
							return (
								<div className="m-radio" key={option.id}>
									<div className="m-radio__field">
										<input
											id={option.id}
											className="m-radio__input"
											checked={formState.option === option.value}
											type="radio"
											name="option"
											value={option.value}
											onChange={selectBearingOption}
										/>
										<label className="m-radio__label" htmlFor={option.id}>
											{option.label}

											{option.value === 'ISO' && (
												<span className="a-tooltip">
													<span>
														<svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path d="M12 8.03261V7M12 16.5L12 9.27174M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#2C2929"></path>
														</svg>
													</span>
													<div className="a-tooltip__text a-tooltip__text--left">
														{getTooltip( option.label )}
													</div>
												</span>
											)}
										</label>
									</div>
								</div>
							);
						} )}
					</div>

					<div className="t-engineering-tools__form-fields t-engineering-tools__form-fields--multi-column timken-tool-presearch-container">
						<TextInput
							id="bearing_fatigue_part_number"
							required={true}
							label={text.partNumber}
							verified={!! partState.partNumber}
							name="partNumber"
							value={searchInput}
							disabled={!! partState.partNumber}
							placeholder={text.partNumber}
							message={
								partState.partNumber
									? `${ text.bearingType }: ${ partState.bearingTypeLabel }`
									: text.partWildCardHint
							}
							onChange={inputPartNumber}
						/>

						<PartPreSearch
							input={searchInput}
							isHidden={searchInput.length < 2 || isPartOpen || isPartSelected}
							unit={formState.unit}
							onClick={preSearchClickHandler}
							verifyPartNumber={(value: string, e) => setPartFromPreSearchAndSubmit(value, e)}
						/>

						<SelectDropdown
							id="bearing_fatigue_unit"
							name="unit"
							required={true}
							label={text.displayUnits}
							value={formState.unit}
							options={unitList}
							onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
								dispatch( {
									type: 'change_unit',
									payload: event.target.value as TimkenUnit,
								} )
							}
						/>

						{ partState.partNumber && (
							<button
								className="btn btn--secondary"
								type="button"
								onClick={() => clearPn()}
							>
								{text.clearPn}
							</button>
						)}

						{! partState.partNumber && (
							<button
								className="btn btn--primary"
								type="submit"
								disabled={! searchInput}
							>
								{text.lookup}
							</button>
						)}
					</div>

					{!! partState.partNumber && (
						<>
							{formState.option === 'Timken' && (
								<div className="t-engineering-tools__constraints">
									<h3>{text.applyLifeAdjustments}</h3>
									<p className="t-engineering-tools__description">
										{text.lifeAdjustmentsDescription}
									</p>
									<div className="row">
										<div className="col-md-3">
											<SelectDropdown
												id="bearing_life_adjustment"
												name="lifeAdjustmentFactor"
												required={true}
												label={text.lifeAdjustments}
												tooltip={getTooltip( text.lifeAdjustments )}
												value={formState.lifeAdjustmentFactor}
												options={lifeAdjustmentList}
												onChange={selectLifeAdjustment}
											/>
										</div>
									</div>
								</div>
							)}

							<div className="t-engineering-tools__constraints">
								<h3>{text.inputs}</h3>
								<p className="t-engineering-tools__description">
									{text.constraintsDescription}
								</p>
								<div className="row">
									<div className="col-md-3">
										<NumberInput
											id="bearing_fatigue_speed"
											required={true}
											label={text.rotationalSpeed}
											unitLabel="rpm"
											value={formState.rotationalSpeed}
											min={0}
											invalid={
												formState.isValidated &&
												! isNonZero( formState.rotationalSpeed )
											}
											errorMessage={text.errorNonZero}
											onChange={( event: ChangeEvent<HTMLInputElement> ) =>
												dispatch( {
													type: 'change_number_field',
													payload: [ 'rotationalSpeed', event.target.value ],
												} )
											}
										/>
									</div>

									<div className="col-md-3">
										<NumberInput
											id="bearing_fatigue_radial_load"
											name="radialLoad"
											required={partState.bearingType === BearingTypeCode.CRB}
											label={text.radialLoad}
											unitLabel={formState.unit === 'M' ? 'N' : 'lbf'}
											tooltip={getTooltip( text.radialLoad )}
											value={formState.radialLoad}
											min={0}
											invalid={formState.isValidated && ! formState.isLoadValid}
											errorMessage={
												partState.bearingType === BearingTypeCode.CRB
													? text.errorNonZero
													: text.errorFatigueLoad
											}
											onChange={( event: ChangeEvent<HTMLInputElement> ) =>
												dispatch( {
													type: 'change_radial_load',
													payload: event.target.value,
												} )
											}
										/>
									</div>

									{partState.bearingType !== BearingTypeCode.CRB && (
										<div className="col-md-3">
											<NumberInput
												id="bearing_fatigue_axial_load"
												name="axialLoad"
												label={text.axialLoad}
												unitLabel={formState.unit === 'M' ? 'N' : 'lbf'}
												tooltip={getTooltip( text.axialLoad )}
												value={formState.axialLoad}
												min={0}
												invalid={
													formState.isValidated && ! formState.isLoadValid
												}
												errorMessage={text.errorFatigueLoad}
												onChange={( event: ChangeEvent<HTMLInputElement> ) =>
													dispatch( {
														type: 'change_axial_load',
														payload: event.target.value,
													} )
												}
											/>
										</div>
									)}

									{formState.isAdjustmentRequired && (
										<>
											<div className="col-md-3">
												<NumberInput
													id="bearing_fatigue_temperature"
													name="operatingTemp"
													label={text.operatingTemp}
													unitLabel={formState.unit === 'M' ? '°C' : '°F'}
													value={formState.operatingTemp}
													required={true}
													invalid={
														formState.isValidated && (! formState.isTempValid || formState.operatingTemp === undefined)
													}
													errorMessage={formState.unit === 'M' ? text.errorOperatingTempC : text.errorOperatingTempF}
													onChange={( event: ChangeEvent<HTMLInputElement> ) =>
														dispatch( {
															type: 'change_operating_temp',
															payload: [ 'operatingTemp', event.target.value, formState.unit ],
														} )
													}
												/>
											</div>

											<div className="col-md-3">
												<SelectDropdown
													id="bearing_fatigue_lub_type"
													name="greaseOrOil"
													required={true}
													label={text.lubType}
													value={formState.greaseOrOil}
													options={lubricantType}
													onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
														dispatch( {
															type: 'change_lub_type',
															payload: event.target
																.value as unknown as TimkenLubricantType,
														} )
													}
												/>
											</div>

											<div className="col-md-3">
												<SelectDropdown
													id="bearing_fatigue_lub_name"
													name="lubricantName"
													required={true}
													label={text.lubName}
													value={formState.lubricantName}
													options={lubricantList}
													onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
														selectLubricant( event.target.value )
													}
												/>
											</div>

											{formState.unit === 'M' && (
												<>
													<div className="col-md-3">
														<NumberInput
															id="bearing_fatigue_cst_40"
															name="lubricantCST40"
															label={text.cst40}
															unitLabel={unitMap[ formState.unit ].viscosity}
															value={formState.lubricantCST40}
															required={true}
															invalid={
																formState.isValidated &&
																! isNonZero( formState.lubricantCST40 )
															}
															errorMessage={text.errorNonZero}
															onChange={(
																event: ChangeEvent<HTMLInputElement>,
															) =>
																dispatch( {
																	type: 'change_number_field',
																	payload: [
																		'lubricantCST40',
																		event.target.value,
																	],
																} )
															}
														/>
													</div>

													<div className="col-md-3">
														<NumberInput
															id="bearing_fatigue_cst_100"
															name="lubricantCST100"
															label={text.cst100}
															unitLabel={unitMap[ formState.unit ].viscosity}
															value={formState.lubricantCST100}
															required={true}
															invalid={
																formState.isValidated &&
																! isNonZero( formState.lubricantCST100 )
															}
															errorMessage={text.errorNonZero}
															onChange={(
																event: ChangeEvent<HTMLInputElement>,
															) =>
																dispatch( {
																	type: 'change_number_field',
																	payload: [
																		'lubricantCST100',
																		event.target.value,
																	],
																} )
															}
														/>
													</div>
												</>
											)}

											{formState.unit === 'I' && (
												<>
													<div className="col-md-3">
														<NumberInput
															id="bearing_fatigue_sus_100"
															name="lubricantSUS100"
															label={text.sus100}
															unitLabel={unitMap[ formState.unit ].viscosity}
															value={formState.lubricantSUS100}
															required={true}
															invalid={
																formState.isValidated &&
																! isNonZero( formState.lubricantSUS100 )
															}
															errorMessage={text.errorNonZero}
															onChange={(
																event: ChangeEvent<HTMLInputElement>,
															) =>
																dispatch( {
																	type: 'change_number_field',
																	payload: [
																		'lubricantSUS100',
																		event.target.value,
																	],
																} )
															}
														/>
													</div>

													<div className="col-md-3">
														<NumberInput
															id="bearing_fatigue_sus_210"
															name="lubricantSUS210"
															label={text.sus210}
															unitLabel={unitMap[ formState.unit ].viscosity}
															value={formState.lubricantSUS210}
															required={true}
															invalid={
																formState.isValidated &&
																! isNonZero( formState.lubricantSUS210 )
															}
															errorMessage={text.errorNonZero}
															onChange={(
																event: ChangeEvent<HTMLInputElement>,
															) =>
																dispatch( {
																	type: 'change_number_field',
																	payload: [
																		'lubricantSUS210',
																		event.target.value,
																	],
																} )
															}
														/>
													</div>
												</>
											)}
										</>
									)}

									{formState.option === 'ISO' && (
										<div className="col-md-3">
											<NumberInput
												id="bearing_fatigue_contamination"
												name="contaminationFactor"
												label={text.contaminationFactor}
												tooltip={getTooltip( text.contaminationFactor )}
												value={formState.contaminationFactor}
                                                showModal={true}
                                                modalContent={<FatigueLifeModal id="contaminationFactor" />}
												required={true}
												invalid={
													formState.isValidated &&
													! isValidRange(
														formState.contaminationFactor,
														CONTAMINATION_FACTOR_MIN,
														CONTAMINATION_FACTOR_MAX,
													)
												}
												errorMessage={text.errorFatigueContamination}
												onChange={( event: ChangeEvent<HTMLInputElement> ) =>
													dispatch( {
														type: 'change_number_field',
														payload: [
															'contaminationFactor',
															event.target.value,
														],
													} )
												}
											/>
										</div>
									)}
								</div>
							</div>
						</>
					)}

					{!! partState.partNumber && (
						<div>
							<div className="t-engineering-tools__terms-of-use"
								  dangerouslySetInnerHTML={{ __html: text.calculateTermsOfUse }}
							/>
							<div className="t-engineering-tools__search-actions">
								<button
									className="btn btn--primary"
									type="submit"
									disabled={formState.isValidated}
								>
									{text.calculate}
								</button>

								<button
									className="btn btn--secondary"
									type="button"
									onClick={() => clearInputs()}
								>
									{text.clearInputs}
								</button>
							</div>
						</div>
					)}
				</form>
			</div>

			{isCalculated && (
				<CalculationResults
					data={results}
					label={dimensionLabel}
					description={text.fatigueLifeResultsText}
					loading={isResultsLoading}
				/>
			)}

			{isPartOpen && !! searchInput && (
				<PartSearch
					units={formState.unit}
					partNumber={searchInput}
					onSelect={verifyPart}
					onClose={() => setIsPartOpen( false )}
				/>
			)}
		</>
	);
};

export default FatigueLife;
